import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';

export interface UploadOptions {
  onProgress?: (progress: number) => void;
  timeout?: number;
}

export interface UploadResult {
  url: string;
  success: boolean;
  error?: string;
}

class UploadService {
  private useS3Upload(): boolean {
    return process.env.REACT_APP_USE_S3_UPLOAD === 'true';
  }

  async uploadFile(file: File, options: UploadOptions = {}): Promise<string> {
    if (this.useS3Upload()) {
      return this.uploadToS3(file, options);
    } else {
      return this.uploadToCloudinary(file, options);
    }
  }

  private async uploadToCloudinary(file: File, options: UploadOptions): Promise<string> {
    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
    const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

    if (!cloudName || !uploadPreset) {
      throw new Error('Cloudinary configuration is missing. Please check environment variables.');
    }

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    uploadFormData.append('upload_preset', uploadPreset);
    uploadFormData.append('resource_type', 'raw'); // For non-image files like PDFs

    const { onProgress, timeout = 30000 } = options;

    let progressInterval: NodeJS.Timeout | null = null;
    
    try {
      // Simulate progressive upload for better UX
      if (onProgress) {
        onProgress(10);
        progressInterval = setInterval(() => {
          // This is a simulation - real progress would require xhr.upload.onprogress
          // but fetch API doesn't support upload progress tracking
        }, 400);
      }

      // Add timeout to prevent hanging
      const uploadPromise = fetch(`https://api.cloudinary.com/v1_1/${cloudName}/raw/upload`, {
        method: 'POST',
        body: uploadFormData,
      });

      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Upload timeout - please try again')), timeout);
      });

      const response = await Promise.race([uploadPromise, timeoutPromise]);

      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }

      if (onProgress) {
        onProgress(95);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Upload failed. Please try again.');
      }

      const data = await response.json();
      
      if (onProgress) {
        onProgress(100);
        // Brief delay to show 100% completion
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      return data.secure_url;
    } catch (error) {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      console.error('Cloudinary upload error:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to upload file to Cloudinary');
    }
  }

  private async uploadToS3(file: File, options: UploadOptions): Promise<string> {
    const accessKeyId = process.env.REACT_APP_AWS_ACCESS_KEY_ID;
    const secretAccessKey = process.env.REACT_APP_AWS_SECRET_ACCESS_KEY;
    const region = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    const bucketName = process.env.REACT_APP_AWS_S3_BUCKET;

    if (!accessKeyId || !secretAccessKey || !bucketName) {
      throw new Error('AWS S3 configuration is missing. Please check environment variables.');
    }

    const { onProgress, timeout = 30000 } = options;

    try {
      const s3Client = new S3Client({
        region,
        credentials: {
          accessKeyId,
          secretAccessKey,
        },
      });

      // Generate unique filename to prevent conflicts
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 15);
      const fileExtension = file.name.split('.').pop();
      const key = `uploads/${timestamp}-${randomString}.${fileExtension}`;

      if (onProgress) {
        onProgress(10);
      }

      // Convert File to Uint8Array for browser compatibility
      const fileBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(fileBuffer);
      
      const command = new PutObjectCommand({
        Bucket: bucketName,
        Key: key,
        Body: uint8Array,
        ContentType: file.type,
        // Note: ACL removed because bucket policy handles public access
        CacheControl: 'max-age=31536000', // Cache for 1 year
      });

      if (onProgress) {
        onProgress(50);
      }

      // Upload file to S3
      await Promise.race([
        s3Client.send(command),
        new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Upload timeout - please try again')), timeout);
        })
      ]);

      if (onProgress) {
        onProgress(95);
      }

      // Construct public URL
      const publicUrl = `https://${bucketName}.s3.${region}.amazonaws.com/${key}`;

      if (onProgress) {
        onProgress(100);
        // Brief delay to show 100% completion
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      return publicUrl;
    } catch (error) {
      console.error('S3 upload error:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to upload file to S3');
    }
  }

  // Validate file before upload
  validateFile(file: File, maxSizeMB = 10): { isValid: boolean; error?: string } {
    const maxSize = maxSizeMB * 1024 * 1024; // Convert to bytes
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (file.size > maxSize) {
      return {
        isValid: false,
        error: `File size must be less than ${maxSizeMB}MB`
      };
    }
    
    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: 'Only PDF and Word documents are supported'
      };
    }
    
    return { isValid: true };
  }

  // Get upload provider info for debugging
  getUploadProvider(): string {
    return this.useS3Upload() ? 'AWS S3' : 'Cloudinary';
  }

  // Upload an image (avatar, logo, etc.) — same plumbing as uploadFile but
  // uses Cloudinary's `image` resource type so the URL supports image
  // transformations (e.g. w_120,h_120,c_fill,g_face for thumbnails).
  // Falls back to S3 with the same code path as uploadFile.
  async uploadImage(file: File, options: UploadOptions = {}): Promise<string> {
    if (!file.type.startsWith('image/')) {
      throw new Error('Selected file is not an image.');
    }
    if (this.useS3Upload()) {
      return this.uploadToS3(file, options);
    }
    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
    const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
    if (!cloudName || !uploadPreset) {
      throw new Error('Cloudinary configuration is missing. Please check environment variables.');
    }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('upload_preset', uploadPreset);
    fd.append('resource_type', 'image');
    const { onProgress, timeout = 30000 } = options;
    if (onProgress) onProgress(10);
    const uploadPromise = fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
      method: 'POST',
      body: fd,
    });
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Upload timeout - please try again')), timeout),
    );
    const response = await Promise.race([uploadPromise, timeoutPromise]);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || 'Image upload failed.');
    }
    const data = await response.json();
    if (onProgress) onProgress(100);
    return data.secure_url as string;
  }
}

// Export singleton instance
export const uploadService = new UploadService();
export default uploadService;